import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, MessageCircle, ExternalLink, PlusCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useTheme } from '../../contexts/ThemeContext';
import { buildSuggestionPrefill, parseSuggestionDescription } from '../../utils/forumSuggestionPrefill';
import { fetchForumSuggestions } from '../../utils/forumSuggestionsDb';

interface Suggestion {
  id: string;
  name: string;
  url: string;
  software?: string | null;
  category_id: string;
  category_ref?: { name: string } | null;
  category_legacy?: string | null;
  description: string;
  status: 'pending' | 'approved' | 'rejected';
  admin_notes: string | null;
  created_at: string;
}

const SuggestionList = () => {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const { darkMode } = useTheme();

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      const data = await fetchForumSuggestions();
      setSuggestions(
        data.map((row) => ({
          id: row.id,
          name: row.name,
          url: row.url || '',
          software: row.software ?? null,
          category_id: row.category_id,
          category_ref: row.category_ref ?? null,
          category_legacy: row.category ?? null,
          description: row.description || '',
          status: row.status as Suggestion['status'],
          admin_notes: row.admin_notes,
          created_at: row.created_at,
        }))
      );
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      toast.error('Öneriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (id: string, notes?: string) => {
    try {
      const { error } = await supabase
        .from('forum_suggestions')
        .update({
          status: 'rejected',
          admin_notes: notes || adminNotes || null,
        })
        .eq('id', id);

      if (error) throw error;

      toast.success('Forum önerisi reddedildi');
      setSelectedSuggestion(null);
      setAdminNotes('');
      await fetchSuggestions();
    } catch (error) {
      console.error('Error updating suggestion:', error);
      toast.error('Öneri durumu güncellenirken bir hata oluştu');
    }
  };

  const handleApproveAndAdd = (suggestion: Suggestion) => {
    const prefill = buildSuggestionPrefill({
      id: suggestion.id,
      name: suggestion.name,
      url: suggestion.url,
      software: suggestion.software,
      description: suggestion.description,
      category: suggestion.category_ref,
      category_legacy: suggestion.category_legacy,
    });

    navigate('/admin/forums', {
      state: { suggestionPrefill: prefill },
    });

    toast.success('Forum ekleme paneli açılıyor — bilgiler dolduruldu');
    setSelectedSuggestion(null);
    setAdminNotes('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Forum Önerileri</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Onayladığınız istekler forum ekleme panelinde otomatik doldurulur.
          </p>
        </div>
      </div>

      <div className="grid gap-6">
        {suggestions.length === 0 && (
          <p className="text-gray-500 dark:text-gray-400">Henüz öneri yok.</p>
        )}
        {suggestions.map((suggestion) => {
          const { submitter, body } = parseSuggestionDescription(suggestion.description);
          const categoryLabel = suggestion.category_ref?.name || suggestion.category_legacy || '—';

          return (
            <div
              key={suggestion.id}
              className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 shadow-sm`}
            >
              <div className="flex justify-between items-start gap-4">
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-semibold mb-2">{suggestion.name}</h3>
                  <div className="space-y-2">
                    {suggestion.url && (
                      <p className="text-sm">
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>URL: </span>
                        <a
                          href={suggestion.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-500 hover:text-indigo-600 inline-flex items-center gap-1 break-all"
                        >
                          {suggestion.url}
                          <ExternalLink size={14} className="shrink-0" />
                        </a>
                      </p>
                    )}
                    <p className="text-sm">
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Kategori: </span>
                      {categoryLabel}
                    </p>
                    {suggestion.software && (
                      <p className="text-sm">
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Yazılım: </span>
                        {suggestion.software}
                      </p>
                    )}
                    {submitter && (
                      <p className="text-sm">
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Öneren: </span>
                        {submitter}
                      </p>
                    )}
                    <p className="text-sm">
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Tarih: </span>
                      {format(new Date(suggestion.created_at), 'dd MMMM yyyy HH:mm', { locale: tr })}
                    </p>
                    {body && (
                      <p className="text-sm whitespace-pre-wrap mt-2 text-gray-600 dark:text-gray-300">
                        {body}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 shrink-0">
                  {suggestion.status === 'pending' ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setSelectedSuggestion(suggestion)}
                        className="p-2 text-gray-500 hover:text-indigo-500 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Not ekle / reddet"
                      >
                        <MessageCircle size={20} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleApproveAndAdd(suggestion)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                        title="Forum ekleme panelini aç ve alanları doldur"
                      >
                        <PlusCircle size={18} />
                        Onayla ve ekle
                      </button>
                      <button
                        type="button"
                        onClick={() => handleReject(suggestion.id)}
                        className="p-2 text-gray-500 hover:text-red-500 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Reddet"
                      >
                        <X size={20} />
                      </button>
                    </>
                  ) : (
                    <span
                      className={`px-3 py-1 rounded-full text-sm ${
                        suggestion.status === 'approved'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}
                    >
                      {suggestion.status === 'approved' ? 'Onaylandı' : 'Reddedildi'}
                    </span>
                  )}
                </div>
              </div>

              {suggestion.admin_notes && (
                <div className="mt-4 pt-4 border-t dark:border-gray-700">
                  <p className="text-sm">
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Admin notu: </span>
                    {suggestion.admin_notes}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selectedSuggestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div
            className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 max-w-lg w-full`}
          >
            <h3 className="text-lg font-semibold mb-2">İşlem: {selectedSuggestion.name}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Reddetmek için not yazabilir veya doğrudan onaylayıp forum ekleme paneline gidebilirsiniz.
            </p>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={4}
              className={`w-full px-4 py-2 rounded-lg border ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
              }`}
              placeholder="Red sebebi veya ek notlar..."
            />
            <div className="flex flex-wrap justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={() => {
                  setSelectedSuggestion(null);
                  setAdminNotes('');
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={() => handleReject(selectedSuggestion.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Reddet
              </button>
              <button
                type="button"
                onClick={() => handleApproveAndAdd(selectedSuggestion)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 inline-flex items-center gap-2"
              >
                <PlusCircle size={18} />
                Onayla ve ekle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuggestionList;
